import { ArrowDown, ArrowUp, Minus } from 'lucide-react'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export type EngagementFactor = {
  counted: boolean
  earned: number | null
  possible: number | null
  reason?: string
  fact?: string
}

export type EngagementDetail = {
  score: number | null
  band: 'New' | 'Engaged' | 'Drifting' | 'Disengaged'
  as_of: string
  stale?: boolean
  change_13_weeks?: number | null
  breakdown: Record<string, EngagementFactor> | null
  trend: Array<{ as_of: string; score: number; band: Exclude<EngagementDetail['band'], 'New'> }>
  weight_change_dates: string[]
}

const BAND_VARIANTS: Record<EngagementDetail['band'], BadgeProps['variant']> = {
  New: 'neutral',
  Engaged: 'success',
  Drifting: 'warning',
  Disengaged: 'destructive',
}

const GRIDLINES = [0, 20, 40, 60, 80, 100]
const FACTOR_ORDER = [
  'Membership tenure',
  'Event attendance',
  'Portal presence',
  'Email engagement',
  'Giving',
]
const BAND_DOT_CLASSES = {
  Engaged: 'fill-success',
  Drifting: 'fill-warning',
  Disengaged: 'fill-destructive',
} as const

function scoreBadge(band: EngagementDetail['band']) {
  return <Badge variant={BAND_VARIANTS[band]}>{band}</Badge>
}

function TrendChart({ detail }: { detail: EngagementDetail }) {
  const width = 1040
  const height = 244
  const x = (index: number) => 32 + (index / Math.max(detail.trend.length - 1, 1)) * 1000
  const y = (score: number) => 16 + ((100 - score) / 100) * 200
  const points = detail.trend.map((point, index) => `${x(index)},${y(point.score)}`).join(' ')

  return (
    <svg
      aria-label="Engagement score, weekly, last 24 months"
      className="block w-full"
      height={height}
      preserveAspectRatio="none"
      role="img"
      viewBox={`0 0 ${width} ${height}`}
    >
      {GRIDLINES.map((value) => (
        <g key={value}>
          <line className="stroke-border" x1="32" x2="1032" y1={y(value)} y2={y(value)} />
          <text
            className="fill-muted-foreground text-caption"
            textAnchor="end"
            x="24"
            y={y(value) + 4}
          >
            {value}
          </text>
        </g>
      ))}
      {detail.weight_change_dates.map((date) => {
        const index = detail.trend.findIndex((point) => point.as_of === date)
        if (index < 0) return null
        return (
          <g key={date}>
            <line
              className="stroke-muted-foreground"
              strokeDasharray="4 3"
              x1={x(index)}
              x2={x(index)}
              y1="16"
              y2="216"
            />
            <text className="fill-muted-foreground text-caption" x={x(index) + 6} y="26">
              Weights changed
            </text>
          </g>
        )
      })}
      <polyline className="fill-none stroke-muted-foreground" points={points} strokeWidth="1" />
      {detail.trend.map((point, index) => (
        <circle
          key={point.as_of}
          className={BAND_DOT_CLASSES[point.band]}
          cx={x(index)}
          cy={y(point.score)}
          r="2"
        >
          <title>{`${point.as_of} · ${point.score} · ${point.band}`}</title>
        </circle>
      ))}
    </svg>
  )
}

function TrendDirection({ change }: { change: number | null | undefined }) {
  if (change === null || change === undefined) return null
  const Icon = change > 0 ? ArrowUp : change < 0 ? ArrowDown : Minus
  const tone =
    change > 0 ? 'text-success' : change < 0 ? 'text-destructive' : 'text-muted-foreground'
  return (
    <span className={`inline-flex items-center gap-1 ${tone}`}>
      <Icon aria-hidden="true" className="size-3.5" />
      <span className="tabular-nums">{change > 0 ? `+${change}` : change}</span>
      <span className="text-caption text-muted-foreground">13 weeks</span>
    </span>
  )
}

function Breakdown({ detail }: { detail: EngagementDetail }) {
  const rows = FACTOR_ORDER.map((name) => [name, detail.breakdown?.[name]] as const)
  const total = rows.reduce((sum, [, factor]) => sum + (factor?.earned ?? 0), 0)
  const possible = rows.reduce((sum, [, factor]) => sum + (factor?.possible ?? 0), 0)
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Factor</TableHead>
          <TableHead className="text-right">Points</TableHead>
          <TableHead>What counted</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(([name, factor]) => (
          <TableRow key={name}>
            <TableCell className={!factor?.counted ? 'text-muted-foreground' : undefined}>
              {name}
            </TableCell>
            <TableCell className="whitespace-nowrap text-right tabular-nums">
              {factor?.counted ? `${factor.earned} / ${factor.possible}` : '—'}
            </TableCell>
            <TableCell className="text-caption text-muted-foreground">
              {factor?.counted
                ? factor.fact
                : `Not counted — ${factor?.reason ?? 'no qualifying activity'}`}
            </TableCell>
          </TableRow>
        ))}
        <TableRow className="border-t border-border font-semibold hover:bg-transparent">
          <TableCell>Total</TableCell>
          <TableCell className="text-right tabular-nums">
            {total} / {possible}
          </TableCell>
          <TableCell />
        </TableRow>
      </TableBody>
    </Table>
  )
}

export function EngagementPanel({
  detail,
  chrome = 'bare',
}: {
  detail: EngagementDetail
  chrome?: 'bare' | 'card'
}) {
  const content =
    detail.band === 'New' ? (
      <div className="space-y-2 p-4">
        <div>{scoreBadge(detail.band)}</div>
        <p className="text-body-sm text-foreground">Scored after their first 90 days.</p>
      </div>
    ) : (
      <div className="divide-y divide-border">
        <div className="space-y-1 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-heading-sm font-semibold tabular-nums text-foreground">
                {detail.score}
              </span>
              {scoreBadge(detail.band)}
            </div>
            <TrendDirection change={detail.change_13_weeks} />
          </div>
          <p className="text-caption text-muted-foreground">As of {detail.as_of}</p>
          {detail.stale && (
            <div
              role="alert"
              className="mt-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-body-sm"
            >
              This Engagement score is more than 48 hours old.
            </div>
          )}
        </div>
        <div className="space-y-2 p-4">
          <div className="flex items-baseline justify-between">
            <h3 className="text-body-sm font-medium">Trend</h3>
            <span className="text-caption text-muted-foreground">Weekly · 24 months</span>
          </div>
          <TrendChart detail={detail} />
        </div>
        <div>
          <Breakdown detail={detail} />
        </div>
      </div>
    )
  return chrome === 'card' ? <Card className="overflow-hidden">{content}</Card> : content
}
